/**
 * Group/popup code management functionality.
 * Handles displaying a popup for group/corporate customers to enter their access code,
 * storing the code in cookies, and redirecting with the code as a URL parameter.
 * 
 * @description This module provides:
 * - Popup display for group code entry
 * - Cookie-based persistence for closed state and entered code
 * - URL parameter injection for code forwarding
 * 
 * @requires getCookie, setCookie (global functions)
 * 
 * @example
 * // Automatically initializes on page load
 * groupPopup.init();
 */
let groupPopup = {
  /** @type {HTMLElement|null} Reference to the popup container element */
  popupEl: null,
  /** @type {string|null} Base URL for redirection */
  url: null,

  /**
   * Initializes the group popup functionality.
   * Checks if popup should be shown based on cookies and sets up event listeners.
   * 
   * @returns {void}
   * @sideEffect May open popup, may add code to URL, binds click events
   */
  init: function() {
    let _this = this;
    // Get popup element from DOM
    this.popupEl = document.querySelector('.group-popup--container');
    
    // Check if popup was previously closed
    let popupClosed = getCookie('group-popup-closed');
    // Check if code was previously entered
    let popupCode = getCookie('group-popup-code');

    // Get base URL from window (set by theme)
    this.url = window.group_popup_url;

    // Open popup if it exists and wasn't closed
    if(this.popupEl && !popupClosed) {
      this.openPopup();
    }
    
    // Add code to URL if previously entered
    if(popupCode != null) {
      this.addCodeToURL(popupCode);
    }

    // Set up close button events
    if(this.popupEl) {
      this.popupEl.querySelectorAll('.close, .close-link').forEach(el => {
        el.addEventListener('click', function() {
          _this.closePopup();
        });
      });

      // Set up form submission if present
      if(this.popupEl.querySelector('.group-popup-form') != null) {
        this.popupEl.querySelector('.group-popup-form').addEventListener('submit', function(e) {
          e.preventDefault();
          _this.submitForm();
        });
      }
    }

    // Set up header code input if present
    if(document.querySelector('.group-code-field') != null) {
      document.querySelector('.group-code-field .submit-group-code').addEventListener('click', function(e) {
        e.preventDefault();
        _this.submitHeaderForm();
      });

      // Handle Enter key in input
      document.querySelector('.group-code-field input').onkeydown = function(e){
        if(e.key == 'Enter'){
          _this.submitHeaderForm();
        }
      };
    }
  },

  /**
   * Opens the group popup by adding active class and setting body state.
   * 
   * @returns {void}
   * @sideEffect Adds CSS classes to popup and body
   */
  openPopup: function() {
    this.popupEl.classList.add('active');
    document.querySelector('body').classList.add('popup-open');
  },

  /**
   * Closes the popup and sets a cookie to remember it was closed.
   * Removes code from URL and clears popup state.
   * 
   * @returns {void}
   * @sideEffect Removes CSS classes, sets cookie for 1 day
   */
  closePopup: function() {
    this.popupEl.classList.remove('active');
    document.querySelector('body').classList.remove('popup-open');
    // Remember popup was closed for 1 day
    setCookie('group-popup-closed', true, 1);
  },

  /**
   * Handles form submission from the popup.
   * Validates input, sets cookies, adds code to URL, and closes popup.
   * 
   * @returns {void}
   * @sideEffect Sets cookies, redirects page with code parameter
   */
  submitForm: function() {
    let field = this.popupEl.querySelector('input[name="group-code"]');
    let val = field.value;
    
    if(val.length > 0) {
      // Valid submission
      field.classList.remove('error');
      // Store code and closed state for 1 day
      setCookie('group-popup-code', val, 1);
      setCookie('group-popup-closed', true, 1);
      // Add code to URL and redirect
      this.addCodeToURL(val);
      this.closePopup();
    }
    else {
      // Show error for empty input
      field.classList.add('error');
    }
  },

  /**
   * Handles form submission from the header code input field.
   * Adds code to URL if valid input provided.
   * 
   * @returns {void}
   * @sideEffect May redirect page with code parameter
   */
  submitHeaderForm: function() {
    let field = document.querySelector('.group-code-field input[name="group-code"]');
    let val = field.value;
    
    if(val.length > 0) {
      // Valid - remove error and add code to URL
      field.classList.remove('error');
      this.addCodeToURL(val);
    }
    else {
      // Show error for empty input
      field.classList.add('error');
    }
  },

  /**
   * Adds or updates the 'code' query parameter in the URL and redirects.
   * Uses the base URL stored during initialization.
   * 
   * @param {string} code - The group code to add to URL
   * @returns {void}
   * @sideEffect Redirects browser to new URL with code parameter
   * 
   * @example
   * groupPopup.addCodeToURL('CORP123');
   */
  addCodeToURL: function(code) {
    if ('URLSearchParams' in window) {
      var searchParams = new URLSearchParams(window.location.search)
      searchParams.set("code", code);
      var newRelativePathQuery = this.url + '?' + searchParams.toString();
      window.location.href = newRelativePathQuery;
    }
  }

}

// Initialize on page load
groupPopup.init();
